/**
 * Client-side image pipeline for private place photos (spec §8.5, §5.4.1).
 *
 * - Validates MIME type, file size (≤ 5 MB) and image decodability.
 * - Reads pixel dimensions from the actual decoded image.
 * - Downscales/re-encodes to max 1600px JPEG via canvas when the source is
 *   larger, keeping the aspect ratio. When canvas is unavailable the original
 *   file is kept (graceful skip) as long as it passes the size limit.
 * - Never produces or consumes public URLs; the returned File is uploaded
 *   through repositories.places.uploadImage.
 */
import { makeAppError } from '@/lib/supabase/errors'
import type { AppError } from '@/types'

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024
export const MAX_IMAGE_DIMENSION = 1600
export const JPEG_EXPORT_QUALITY = 0.85
/** Images up to this size may be embedded in the local draft (survive reload). */
export const DRAFT_EMBED_MAX_BYTES = 500 * 1024

export type PreparedImage = {
  file: File
  width: number
  height: number
  /** True when the canvas pipeline re-encoded the image to JPEG. */
  reencoded: boolean
}

function validationError(code: string, userMessage: string, technicalMessage?: string): AppError {
  return makeAppError({
    code,
    userMessage,
    technicalMessage,
    retryable: false,
    source: 'validation',
  })
}

/** Keeps aspect ratio; returns the input unchanged when it already fits. */
export function computeTargetDimensions(
  width: number,
  height: number,
  maxDimension: number = MAX_IMAGE_DIMENSION,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) {
    return { width: maxDimension, height: maxDimension }
  }
  if (width <= maxDimension && height <= maxDimension) {
    return { width, height }
  }
  const scale = maxDimension / Math.max(width, height)
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

export function toJpegFileName(name: string): string {
  const base = name.replace(/\.[^./\\]+$/, '').trim()
  return `${base || 'foto'}.jpg`
}

type DecodedImage = {
  source: CanvasImageSource
  width: number
  height: number
  cleanup: () => void
}

async function decodeWithImageBitmap(file: File): Promise<DecodedImage | null> {
  if (typeof createImageBitmap !== 'function') return null
  try {
    const bitmap = await createImageBitmap(file)
    if (!bitmap.width || !bitmap.height) {
      bitmap.close()
      return null
    }
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      cleanup: () => bitmap.close(),
    }
  } catch {
    return null
  }
}

async function decodeWithImageElement(file: File): Promise<DecodedImage> {
  if (typeof Image === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    throw validationError(
      'IMAGE_DECODE_UNSUPPORTED',
      'Je browser kan deze foto niet lezen. Probeer een andere foto.',
    )
  }
  const objectUrl = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('image element decode failed'))
      el.src = objectUrl
    })
    const width = img.naturalWidth
    const height = img.naturalHeight
    if (!width || !height) {
      throw new Error('image decoded with empty dimensions')
    }
    return {
      source: img,
      width,
      height,
      cleanup: () => URL.revokeObjectURL(objectUrl),
    }
  } catch (error) {
    URL.revokeObjectURL(objectUrl)
    throw error
  }
}

/** Decode check + dimension read. Throws AppError when the file is not a readable image. */
async function decodeImage(file: File): Promise<DecodedImage> {
  const viaBitmap = await decodeWithImageBitmap(file)
  if (viaBitmap) return viaBitmap
  try {
    return await decodeWithImageElement(file)
  } catch (error) {
    if (typeof error === 'object' && error !== null && 'code' in error) throw error
    throw validationError(
      'IMAGE_DECODE_FAILED',
      'Deze foto kan niet worden gelezen. Kies een andere foto.',
      `type=${file.type} size=${file.size}`,
    )
  }
}

async function reencodeToJpeg(decoded: DecodedImage, targetWidth: number, targetHeight: number): Promise<Blob | null> {
  try {
    if (typeof document === 'undefined') return null
    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight
    const context = canvas.getContext('2d')
    if (!context) return null
    context.drawImage(decoded.source, 0, 0, targetWidth, targetHeight)
    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', JPEG_EXPORT_QUALITY)
    })
  } catch {
    // Canvas unavailable or blocked (e.g. privacy mode): graceful skip.
    return null
  }
}

function tooLargeError(): AppError {
  return validationError(
    'IMAGE_TOO_LARGE',
    'De foto is groter dan 5 MB. Kies een kleinere foto.',
  )
}

/**
 * Validates and (when needed) downscales a picked image file.
 * Throws a typed AppError with honest Dutch copy on any validation failure.
 */
export async function prepareImage(input: File): Promise<PreparedImage> {
  if (!input.type.toLowerCase().startsWith('image/')) {
    throw validationError(
      'IMAGE_TYPE_INVALID',
      'Dit bestand is geen foto. Kies een afbeelding.',
      `type=${input.type || 'unknown'}`,
    )
  }

  const decoded = await decodeImage(input)
  try {
    const { width, height } = decoded
    const target = computeTargetDimensions(width, height)
    const withinDimensions = target.width === width && target.height === height
    const withinSize = input.size <= MAX_IMAGE_BYTES

    if (withinDimensions && withinSize) {
      return { file: input, width, height, reencoded: false }
    }

    const jpeg = await reencodeToJpeg(decoded, target.width, target.height)
    if (jpeg) {
      if (jpeg.size > MAX_IMAGE_BYTES) throw tooLargeError()
      return {
        file: new File([jpeg], toJpegFileName(input.name), { type: 'image/jpeg' }),
        width: target.width,
        height: target.height,
        reencoded: true,
      }
    }

    // Canvas unavailable: keep the original only when it already fits the limits.
    if (!withinSize) throw tooLargeError()
    return { file: input, width, height, reencoded: false }
  } finally {
    decoded.cleanup()
  }
}

export function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    if (typeof FileReader === 'undefined') {
      reject(new Error('FileReader is unavailable'))
      return
    }
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(file)
  })
}

export async function dataUrlToFile(dataUrl: string, fileName: string): Promise<File> {
  const response = await fetch(dataUrl)
  const blob = await response.blob()
  return new File([blob], fileName, { type: blob.type || 'image/jpeg' })
}

export type PreviewUrl = {
  url: string
  /** Object URLs must be revoked; data URLs must not. */
  isObjectUrl: boolean
}

/** Local preview via createObjectURL, with FileReader data-URL fallback. */
export async function createPreviewUrl(file: File): Promise<PreviewUrl> {
  if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
    try {
      return { url: URL.createObjectURL(file), isObjectUrl: true }
    } catch {
      // fall through to FileReader
    }
  }
  return { url: await fileToDataUrl(file), isObjectUrl: false }
}
